import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function JobsPage() {
  // Add scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const [activeTab, setActiveTab] = useState('boards');

  const jobBoards = [
    {
      id: 1,
      title: 'IrishJobs.ie',
      description: 'Ireland\'s leading job board with thousands of opportunities across all sectors',
      icon: 'ri-briefcase-line',
      url: 'https://www.irishjobs.ie',
      category: 'General Job Board',
      features: ['CV Database', 'Job Alerts', 'Career Advice']
    },
    {
      id: 2,
      title: 'Jobs.ie',
      description: 'Comprehensive job search platform with detailed company profiles and salary guides',
      icon: 'ri-search-line',
      url: 'https://www.jobs.ie',
      category: 'General Job Board',
      features: ['Salary Calculator', 'Company Reviews', 'Career Resources']
    },
    {
      id: 3,
      title: 'LinkedIn Ireland',
      description: 'Professional networking platform with extensive job listings and recruiter connections',
      icon: 'ri-linkedin-box-line',
      url: 'https://www.linkedin.com/jobs',
      category: 'Professional Network',
      features: ['Networking', 'Direct Applications', 'Recruiter Messages']
    },
    {
      id: 4,
      title: 'Indeed Ireland',
      description: 'Global job search engine with local Irish opportunities and company insights',
      icon: 'ri-global-line',
      url: 'https://ie.indeed.com',
      category: 'Job Search Engine',
      features: ['Job Alerts', 'Company Reviews', 'Salary Comparison']
    },
    {
      id: 5,
      title: 'Glassdoor Ireland',
      description: 'Job listings combined with employee reviews, salary data, and interview insights',
      icon: 'ri-star-line',
      url: 'https://www.glassdoor.ie',
      category: 'Jobs & Reviews',
      features: ['Company Reviews', 'Salary Data', 'Interview Tips']
    },
    {
      id: 6,
      title: 'Recruiters.ie',
      description: 'Connect directly with recruitment agencies specializing in your industry',
      icon: 'ri-team-line',
      url: 'https://www.recruiters.ie',
      category: 'Recruitment Agencies',
      features: ['Agency Directory', 'Specialist Recruiters', 'Career Guidance']
    }
  ];

  const tips = [
    {
      id: 1,
      title: 'Tailor Your CV to Irish Standards',
      description: 'Irish CVs typically include a professional photo, are 2-3 pages long, and emphasize achievements with metrics. Include references or "References available upon request".',
      icon: 'ri-file-text-line',
      color: 'bg-blue-100 text-blue-600'
    },
    {
      id: 2,
      title: 'Understand Work Permits',
      description: 'Non-EU citizens need a work permit. Critical Skills Employment Permit and General Employment Permit are most common. Your employer typically sponsors the application.',
      icon: 'ri-passport-line',
      color: 'bg-emerald-100 text-emerald-600'
    },
    {
      id: 3,
      title: 'Network Actively',
      description: 'Join professional groups on LinkedIn, attend industry meetups, and connect with recruiters. Many jobs in Ireland are filled through networking before being publicly advertised.',
      icon: 'ri-group-line',
      color: 'bg-purple-100 text-purple-600'
    },
    {
      id: 4,
      title: 'Research Salary Expectations',
      description: 'Irish salaries vary by sector and location. Dublin typically pays 10-20% more than other cities. Use salary calculators to understand take-home pay after tax.',
      icon: 'ri-money-euro-circle-line',
      color: 'bg-amber-100 text-amber-600'
    },
    {
      id: 5,
      title: 'Prepare for Irish Interview Style',
      description: 'Irish interviews are professional but friendly. Prepare STAR method examples, research the company culture, and be ready to discuss your visa situation openly.',
      icon: 'ri-question-answer-line',
      color: 'bg-pink-100 text-pink-600'
    },
    {
      id: 6,
      title: 'Consider Contract vs Permanent',
      description: 'Contract roles are common in tech and finance, often paying higher rates. Understand the differences in benefits, job security, and tax implications.',
      icon: 'ri-file-list-line',
      color: 'bg-indigo-100 text-indigo-600'
    }
  ];

  const topEmployers = [
    {
      id: 1,
      name: 'Google',
      industry: 'Technology',
      location: 'Dublin',
      employees: '8,000+',
      logo: 'ri-google-line',
      description: 'European headquarters with roles in engineering, sales, marketing, and operations',
      benefits: ['Competitive salary', 'Health insurance', 'Pension', 'Flexible working']
    },
    {
      id: 2,
      name: 'Meta (Facebook)',
      industry: 'Technology',
      location: 'Dublin',
      employees: '5,000+',
      logo: 'ri-facebook-circle-line',
      description: 'International operations hub with diverse opportunities across multiple teams',
      benefits: ['Stock options', 'Wellness programs', 'Career development', 'Relocation support']
    },
    {
      id: 3,
      name: 'Apple',
      industry: 'Technology',
      location: 'Cork',
      employees: '6,000+',
      logo: 'ri-apple-line',
      description: 'European operations center with manufacturing and customer support roles',
      benefits: ['Employee discounts', 'Health coverage', 'Education assistance', 'Gym membership']
    },
    {
      id: 4,
      name: 'Pfizer',
      industry: 'Pharmaceuticals',
      location: 'Dublin & Cork',
      employees: '4,000+',
      logo: 'ri-medicine-bottle-line',
      description: 'Major pharmaceutical manufacturing and research facilities',
      benefits: ['Comprehensive healthcare', 'Pension plan', 'Life insurance', 'Wellness programs']
    },
    {
      id: 5,
      name: 'Accenture',
      industry: 'Consulting',
      location: 'Dublin',
      employees: '7,000+',
      logo: 'ri-building-line',
      description: 'Global consulting firm with technology, strategy, and operations consulting',
      benefits: ['Training programs', 'Career progression', 'Flexible hours', 'Travel opportunities']
    },
    {
      id: 6,
      name: 'Bank of America',
      industry: 'Financial Services',
      location: 'Dublin',
      employees: '2,000+',
      logo: 'ri-bank-line',
      description: 'European technology and operations hub for banking services',
      benefits: ['Competitive salary', 'Bonus structure', 'Health insurance', 'Pension matching']
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-[#0A381F] to-emerald-900 text-white py-20">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-[#D4AF37]/20 rounded-full mx-auto mb-6">
              <i className="ri-briefcase-4-line text-4xl text-[#D4AF37]"></i>
            </div>
            <h1 className="text-5xl font-bold mb-4">Jobs in Ireland</h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Discover opportunities with curated job boards, community referrals, and top employer insights
            </p>
            
            {/* Navigation Tabs */}
            <div className="flex items-center justify-center gap-2 mt-8">
              <button
                onClick={() => setActiveTab('boards')}
                className={`px-6 py-3 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${
                  activeTab === 'boards'
                    ? 'bg-white text-[#0A381F] shadow-md'
                    : 'bg-[#D4AF37] text-white hover:bg-[#C19B2F]'
                }`}
              >
                <i className="ri-links-line mr-2"></i>
                Job Boards
              </button>
              <button
                onClick={() => setActiveTab('tips')}
                className={`px-6 py-3 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${
                  activeTab === 'tips'
                    ? 'bg-white text-[#0A381F] shadow-md'
                    : 'bg-[#D4AF37] text-white hover:bg-[#C19B2F]'
                }`}
              >
                <i className="ri-lightbulb-line mr-2"></i>
                Job Search Tips
              </button>
              <button
                onClick={() => setActiveTab('employers')}
                className={`px-6 py-3 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${
                  activeTab === 'employers'
                    ? 'bg-white text-[#0A381F] shadow-md'
                    : 'bg-[#D4AF37] text-white hover:bg-[#C19B2F]'
                }`}
              >
                <i className="ri-building-line mr-2"></i>
                Top Employers
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Job Boards Tab */}
          {activeTab === 'boards' && (
            <div>
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Top Job Boards in Ireland</h2>
                <p className="text-gray-600">Essential platforms for finding employment opportunities across Ireland</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {jobBoards.map((board) => (
                  <a
                    key={board.id}
                    href={board.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-all hover:border-[#0A381F] cursor-pointer"
                  >
                    <div className="w-12 h-12 bg-[#0A381F]/10 rounded-lg flex items-center justify-center mb-4">
                      <i className={`${board.icon} text-2xl text-[#0A381F]`}></i>
                    </div>
                    <div className="mb-3">
                      <span className="text-xs font-medium text-[#D4AF37] uppercase tracking-wide">{board.category}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{board.title}</h3>
                    <p className="text-gray-600 text-sm mb-4">{board.description}</p>
                    <div className="space-y-1 mb-4">
                      {board.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center text-xs text-gray-600">
                          <i className="ri-check-line text-emerald-600 mr-2"></i>
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center text-[#0A381F] text-sm font-medium">
                      <span>Visit Website</span>
                      <i className="ri-arrow-right-line ml-2"></i>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Tips Tab */}
          {activeTab === 'tips' && (
            <div>
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Job Search Tips for Americans</h2>
                <p className="text-gray-600">Expert advice to help you land your dream job in Ireland</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {tips.map((tip) => (
                  <div key={tip.id} className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 ${tip.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <i className={`${tip.icon} text-2xl`}></i>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{tip.title}</h3>
                        <p className="text-gray-600 text-sm leading-relaxed">{tip.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top Employers Tab */}
          {activeTab === 'employers' && (
            <div>
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Top Employers in Ireland</h2>
                <p className="text-gray-600">Leading companies hiring international talent</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {topEmployers.map((employer) => (
                  <div key={employer.id} className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className={`${employer.logo} text-3xl text-gray-700`}></i>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 mb-1">{employer.name}</h3>
                        <div className="flex items-center gap-3 text-sm text-gray-600">
                          <span className="flex items-center">
                            <i className="ri-building-line mr-1"></i>
                            {employer.industry}
                          </span>
                          <span className="flex items-center">
                            <i className="ri-map-pin-line mr-1"></i>
                            {employer.location}
                          </span>
                        </div>
                      </div>
                    </div>
                    <p className="text-gray-700 text-sm mb-4">{employer.description}</p>
                    <div className="mb-4">
                      <div className="flex items-center text-sm text-gray-600 mb-2">
                        <i className="ri-team-line mr-2"></i>
                        <span className="font-medium">{employer.employees} employees in Ireland</span>
                      </div>
                    </div>
                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-xs font-medium text-gray-700 mb-2">Key Benefits:</p>
                      <div className="flex flex-wrap gap-2">
                        {employer.benefits.map((benefit, idx) => (
                          <span key={idx} className="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full">
                            {benefit}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>

      <Footer />
    </div>
  );
}
